import * as React from 'react';
import OlMap from 'ol/Map';
import OlLayerImage from 'ol/layer/Image';
import OlLayerTile from 'ol/layer/Tile';
import { MapComponent } from '@terrestris/react-geo';
import '../css/MyLayerSwitcher.css';

const _isEqual = require('lodash/isEqual');

/**
 * @export
 * @interface LayerSwitcherProps
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
export interface BaseProps {
    /**
     * The layers to be available in the switcher.
     */
    layers: Array<OlLayerTile | OlLayerImage>;

    /**
     * The main map the layers should be synced with.
     */
    map: OlMap;

    /**
     * Callback to inform of the baseLayer selected
     */
    onBaseLayerChanged?: (layerSelected: OlLayerTile | OlLayerImage) => void;
}

interface LayerSwitcherState {
    previewLayer: OlLayerTile | OlLayerImage;
}

export type LayerSwitcherProps = BaseProps & React.HTMLAttributes<HTMLDivElement>;

/**
 * Class representing the LayerSwitcher.
 * A basic component to switch between the passed layers.
 * This is most likely to be used for the backgroundlayer.
 *
 * @class LayerSwitcher
 * @extends React.Component
 */
export class MyLayerSwitcher extends React.Component<LayerSwitcherProps, LayerSwitcherState> {

    /**
     * The internal map of the LayerSwitcher
     */
    _map: OlMap = null;

    /**
     * Index del Layer que està visible en el mapa gran
     */
    _visibleLayerIndex: number = null;

    /**
     * Llista de capes copiades
     */
    _layerClones: Array<OlLayerTile | OlLayerImage> = [];

    /**
     * The className of this component.
     */
    static readonly _className = 'react-geo-layer-switcher';

    /**
     * Creates the LayerSwitcher.
     *
     * @constructs LayerSwitcher
     */
    constructor(props: LayerSwitcherProps) {
        super(props);
        this._map = this.getMap();
        this.state = {
            previewLayer: null
        };
    }

    /**
     * A react lifecycle method called when the component did mount.
     */
    componentDidMount() {
        this.setMapLayers();
        this.updateLayerVisibility();
    }

    /**
     * Destroy all map specific stuff when umounting the component.
     */
    componentWillUnmount() {
        this._map.getLayers().clear();
        this._map.setTarget(null);
        this._map = null;
    }

    /**
     * Invoked immediately after updating occurs. This method is not called for
     * the initial render.
     *
     * @param prevProps The previous props.
     */
    componentDidUpdate(prevProps: LayerSwitcherProps) {
        if (!(_isEqual(this.props.layers, prevProps.layers))) {
            this.setMapLayers();
            this.updateLayerVisibility();
        }
    }

    /**
     * Clones a layer
     *
     * @param layer The layer to clone.
     * @returns The cloned layer.
     */
    cloneLayer = (layer: OlLayerTile | OlLayerImage): OlLayerTile | OlLayerImage => {
        let layerClone: OlLayerTile | OlLayerImage;
        if (layer.get('singleTile')) {
            layerClone = new OlLayerImage({
                source: (layer as OlLayerImage).getSource(),
                ...layer.getProperties()
            });
        } else {
            layerClone = new OlLayerTile({
                source: (layer as OlLayerTile).getSource(),
                ...layer.getProperties()
            });
        }
        layerClone.set('originalLayer', layer);
        return layerClone;
    }

    /**
     * (Re-)adds the layers to the preview map and sets the visibleLayerIndex.
     */
    setMapLayers = () => {
        const {
            layers
        } = this.props;
        if (layers.length < 2) {
            console.log(`LayerSwitcher requires two or more layers.`);
        }
        this._map.getLayers().clear();
        this._layerClones = layers.map((layer, index) => {
            const layerClone = this.cloneLayer(layer);
            if (layerClone.getVisible()) {
                this._visibleLayerIndex = index;
            }
            this._map.addLayer(layerClone);
            return layerClone;
        });
    }

    /**
     * Sets the visiblity of the layers in the props.map and this._map.
     * Also sets the previewLayer in the state.
     */
    updateLayerVisibility = () => {
        const {
            layers
        } = this.props;
        layers.forEach((l, i) => {
            if (this._visibleLayerIndex === i) {
                l.setVisible(true);
            } else {
                l.setVisible(false);
            }
        });
        this._layerClones.forEach((l, i) => {
            if (this._visibleLayerIndex === this._layerClones.length - 1 && i === 0) {
                l.setVisible(true);
                this.setState({ previewLayer: l });
            } else if (this._visibleLayerIndex + 1 === i) {
                l.setVisible(true);
                this.setState({ previewLayer: l });
            } else {
                l.setVisible(false);
            }
        });
    }

    /**
     * Constructs this._map
     */
    getMap = (): OlMap => {
        const {
            map
        } = this.props;
        return new OlMap({
            view: map.getView(),
            controls: []
        });
    }

    /**
     * Clickhandler for the overview switch.
     */
    onSwitcherClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        evt.stopPropagation();
        this._map.getLayers().getArray().forEach((layer, index: number) => {
            if (layer.getVisible()) {
                this._visibleLayerIndex = index;
            }
        });
        this.updateLayerVisibility();
        if (this.props.onBaseLayerChanged) {
            const {
                layers
            } = this.props;
            this.props.onBaseLayerChanged(layers[this._visibleLayerIndex]);
        }
    }

    /**
     * The render function.
     */
    render() {
        const {
            onBaseLayerChanged,
            ...passThroughProps
        } = this.props;

        return (
            <div
                className={MyLayerSwitcher._className}
                {...passThroughProps}
            >
                <div
                    className="clip"
                    onClick={this.onSwitcherClick}
                >
                    <MapComponent
                        className="lsm"
                        mapDivId="layer-switcher-map"
                        map={this._map}
                    />
                    {
                        this.state.previewLayer &&
                        <span className="layer-title">
                            {this.state.previewLayer.get('name')}
                        </span>
                    }
                </div>
            </div>
        );
    }
}

export default MyLayerSwitcher;